import * as React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import {
    assignDailyQuestDebug,
    cancelDailyQuestInstance,
    createDailyQuestTemplate,
    deleteDailyQuestTemplate,
    getDailyQuestInstances,
    getDailyQuestStatistics,
    getDailyQuestTemplates,
    resolveStrapiMediaUrlForDisplay,
    setDailyQuestTemplateActive,
} from '../Api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DailyQuestAdminInstanceDto from '../models/dtos/DailyQuestAdminInstanceDto';
import DailyQuestTemplateDto, {
    DailyQuestStatus,
    UpsertDailyQuestTemplateDto,
} from '../models/dtos/DailyQuestTemplateDto';
import PaginatedDailyQuestInstancesDto from '../models/dtos/PaginatedDailyQuestInstancesDto';
import DailyQuestDetailsDialog from '../components/DailyQuestDetailsDialog';
import DailyQuestStatisticsDto, { DailyQuestStatisticsTimeframe } from '../models/dtos/DailyQuestStatisticsDto';

const STATUS_OPTIONS: DailyQuestStatus[] = ['PENDING', 'COMPLETED', 'EXPIRED', 'CANCELLED'];
const STAT_TIMEFRAME_OPTIONS: { value: DailyQuestStatisticsTimeframe; label: string }[] = [
    { value: 'LAST_30_DAYS', label: '30 days' },
    { value: 'LAST_7_DAYS', label: '7 days' },
    { value: 'YESTERDAY', label: 'Yesterday' },
    { value: 'TODAY', label: 'Today' },
];

function toCsv(values: string[]) {
    return values.join(', ');
}

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatNumber(value: number | undefined) {
    return (value ?? 0).toLocaleString();
}

function formatPercent(value: number | undefined) {
    return `${(value ?? 0).toFixed(1)}%`;
}

function countryFlag(countryCode?: string | null) {
    const code = countryCode?.trim().toUpperCase();
    if (!code || code.length !== 2) return null;
    return Array.from(code)
        .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
        .join('');
}

function userDisplayName(quest: DailyQuestAdminInstanceDto) {
    return quest.user?.name?.trim() || `User ${quest.userId}`;
}

function dailyQuestTemplateFileName(template: DailyQuestTemplateDto) {
    const name = template.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return `${name || 'daily-quest-template'}.json`;
}

function importedTemplateToCreatePayload(value: unknown): UpsertDailyQuestTemplateDto {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Import file must contain a daily quest template JSON object.');
    }
    const template = value as Partial<DailyQuestTemplateDto & UpsertDailyQuestTemplateDto>;
    if (typeof template.name !== 'string' || !Array.isArray(template.taskPool)) {
        throw new Error('Import file does not contain a valid daily quest template.');
    }

    return {
        name: template.name,
        coverImageUrl: template.coverImageUrl,
        titleLocalizations: template.titleLocalizations ?? {},
        descriptionLocalizations: template.descriptionLocalizations ?? {},
        activationEvents: template.activationEvents ?? [],
        requiredTags: template.requiredTags ?? [],
        rewardBetcoins: template.rewardBetcoins as number,
        tasksToAssignCount: template.tasksToAssignCount as number,
        taskPool: template.taskPool.map(task => ({ ...task, id: null })),
        active: template.active as boolean,
    };
}

function tabFromQuery(value: string | null) {
    if (value === 'history') return 1;
    if (value === 'statistics') return 2;
    return 0;
}

function tabToQuery(value: number) {
    if (value === 1) return 'history';
    if (value === 2) return 'statistics';
    return 'templates';
}

function timeframeFromQuery(value: string | null): DailyQuestStatisticsTimeframe {
    return STAT_TIMEFRAME_OPTIONS.some(option => option.value === value)
        ? value as DailyQuestStatisticsTimeframe
        : 'LAST_30_DAYS';
}

function StatCard({ label, value }: { label: string; value: string | number }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ mt: 0.5 }}>{value}</Typography>
            </CardContent>
        </Card>
    );
}

export default function DailyQuestsAdminPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = React.useState(() => tabFromQuery(searchParams.get('tab')));
    const [templates, setTemplates] = React.useState<DailyQuestTemplateDto[]>([]);
    const [instancesPage, setInstancesPage] = React.useState<PaginatedDailyQuestInstancesDto | null>(null);
    const [filterUserId, setFilterUserId] = React.useState('');
    const [filterStatus, setFilterStatus] = React.useState<DailyQuestStatus | ''>('');
    const [filterRewardClaimed, setFilterRewardClaimed] = React.useState<'' | 'true' | 'false'>('');
    const [statistics, setStatistics] = React.useState<DailyQuestStatisticsDto | null>(null);
    const [statisticsTemplateId, setStatisticsTemplateId] = React.useState(() => {
        const queryTemplateId = searchParams.get('templateId');
        return queryTemplateId && queryTemplateId !== 'all' ? queryTemplateId : '';
    });
    const [statisticsTimeframe, setStatisticsTimeframe] = React.useState<DailyQuestStatisticsTimeframe>(() => timeframeFromQuery(searchParams.get('timeframe')));
    const [statisticsLoading, setStatisticsLoading] = React.useState(false);
    const [selectedQuestId, setSelectedQuestId] = React.useState<string | null>(null);
    const [debugUserId, setDebugUserId] = React.useState('');
    const [debugTemplateId, setDebugTemplateId] = React.useState('');
    const [message, setMessage] = React.useState<string | null>(null);
    const [importingTemplate, setImportingTemplate] = React.useState(false);

    const loadTemplates = React.useCallback(async () => {
        setTemplates(await getDailyQuestTemplates(false) ?? []);
    }, []);

    const loadInstances = React.useCallback(async () => {
        const page = await getDailyQuestInstances(
            filterUserId.trim() ? Number(filterUserId.trim()) : undefined,
            filterStatus || undefined,
            filterRewardClaimed === '' ? undefined : filterRewardClaimed === 'true',
            0,
            50,
        );
        setInstancesPage(page);
    }, [filterRewardClaimed, filterStatus, filterUserId]);

    const loadStatistics = React.useCallback(async () => {
        setStatisticsLoading(true);
        try {
            setStatistics(await getDailyQuestStatistics(statisticsTemplateId || undefined, statisticsTimeframe));
        } finally {
            setStatisticsLoading(false);
        }
    }, [statisticsTemplateId, statisticsTimeframe]);

    React.useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    React.useEffect(() => {
        loadInstances();
    }, [loadInstances]);

    React.useEffect(() => {
        loadStatistics();
    }, [loadStatistics]);

    React.useEffect(() => {
        const next = new URLSearchParams();
        next.set('tab', tabToQuery(tab));
        if (tab === 2) {
            next.set('templateId', statisticsTemplateId || 'all');
            next.set('timeframe', statisticsTimeframe);
        }
        setSearchParams(next, { replace: true });
    }, [setSearchParams, statisticsTemplateId, statisticsTimeframe, tab]);

    const triggerDebugAssignment = async () => {
        if (!debugUserId || !debugTemplateId) return;
        const assigned = await assignDailyQuestDebug({ userId: Number(debugUserId), templateId: debugTemplateId });
        setMessage(assigned ? 'Daily quest assigned.' : 'Daily quest was not assigned.');
        await loadInstances();
    };

    const cancelQuest = async (quest: DailyQuestAdminInstanceDto) => {
        const result = await cancelDailyQuestInstance(quest.id);
        setMessage(result.ok ? 'Quest cancelled.' : (result.error ?? 'Quest was not cancelled.'));
        await loadInstances();
    };

    const openTemplateStatistics = (templateId: string) => {
        setStatisticsTemplateId(templateId);
        setTab(2);
    };

    const exportTemplate = (template: DailyQuestTemplateDto) => {
        const content = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = dailyQuestTemplateFileName(template);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(downloadUrl);
    };

    const importTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setImportingTemplate(true);
        try {
            const payload = importedTemplateToCreatePayload(JSON.parse(await file.text()));
            const result = await createDailyQuestTemplate(payload);
            if (!result.ok) {
                setMessage(result.error ?? 'Template was not imported.');
                return;
            }
            setMessage('Template imported as a new template.');
            await loadTemplates();
        } catch (error) {
            setMessage(error instanceof Error ? error.message : 'Template was not imported.');
        } finally {
            setImportingTemplate(false);
        }
    };

    const title = (quest: DailyQuestAdminInstanceDto) => quest.titleLocalizations.en || quest.templateName;

    return (
        <Box sx={{ mx: 4, my: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Daily quests</Typography>
            {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
                <Tab label="Templates" />
                <Tab label="Quests History" />
                <Tab label="Statistics" />
            </Tabs>

            {tab === 0 && (
                <Stack gap={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Templates</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadFileOutlinedIcon />}
                                disabled={importingTemplate}
                            >
                                {importingTemplate ? 'Importing...' : 'Import'}
                                <input hidden type="file" accept="application/json,.json" onChange={importTemplate} />
                            </Button>
                            <Button variant="contained" onClick={() => navigate('/marketing/daily-quests/templates/new')}>New template</Button>
                        </Stack>
                    </Stack>
                    <Stack gap={1.5}>
                        {templates.map(template => (
                            <Card key={template.id} variant="outlined">
                                <CardContent>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        {template.coverImageUrl && (
                                            <Box
                                                component="img"
                                                src={resolveStrapiMediaUrlForDisplay(template.coverImageUrl) ?? template.coverImageUrl}
                                                alt=""
                                                sx={{ width: 120, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                            />
                                        )}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="h6">{template.name}</Typography>
                                                <Chip size="small" label={template.active ? 'enabled' : 'disabled'} color={template.active ? 'success' : 'default'} />
                                                <Chip size="small" label={`${template.tasksToAssignCount}/${template.taskPool.length} tasks`} />
                                                <Chip
                                                    size="small"
                                                    label={`${template.taskPool.filter(task => task.userTaskTemplateImplemented).length}/${template.taskPool.length} implemented`}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">{toCsv(template.activationEvents) || 'No activation event'}</Typography>
                                            <Typography variant="body2" color="text.secondary">Tags: {toCsv(template.requiredTags) || '-'}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                                <CardActions>
                                    <Button onClick={() => navigate('/marketing/daily-quests/templates/' + encodeURIComponent(template.id) + '/edit')}>Edit</Button>
                                    <Button onClick={() => openTemplateStatistics(template.id)}>Statistics</Button>
                                    <Button startIcon={<FileDownloadOutlinedIcon />} onClick={() => exportTemplate(template)}>Export</Button>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={template.active}
                                                onChange={async event => {
                                                    const result = await setDailyQuestTemplateActive(template.id, event.target.checked);
                                                    if (!result.ok) {
                                                        setMessage(result.error ?? 'Template was not updated.');
                                                    }
                                                    await loadTemplates();
                                                }}
                                            />
                                        }
                                        label={template.active ? 'Enabled' : 'Disabled'}
                                    />
                                    <Button color="error" onClick={async () => {
                                        const result = await deleteDailyQuestTemplate(template.id);
                                        setMessage(result.ok ? 'Template deleted.' : (result.error ?? 'Template was not deleted.'));
                                        await loadTemplates();
                                    }}>
                                        Delete
                                    </Button>
                                </CardActions>
                            </Card>
                        ))}
                    </Stack>
                </Stack>
            )}

            {tab === 1 && (
                <Stack gap={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <TextField label="User ID" value={filterUserId} onChange={e => setFilterUserId(e.target.value)} />
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={filterStatus} onChange={e => setFilterStatus(e.target.value as DailyQuestStatus | '')}>
                                <MenuItem value="">All</MenuItem>
                                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Reward claimed</InputLabel>
                            <Select label="Reward claimed" value={filterRewardClaimed} onChange={e => setFilterRewardClaimed(e.target.value as '' | 'true' | 'false')}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="true">true</MenuItem>
                                <MenuItem value="false">false</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={loadInstances}>Refresh</Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <TextField label="Debug user ID" value={debugUserId} onChange={e => setDebugUserId(e.target.value)} />
                        <FormControl sx={{ minWidth: 260 }}>
                            <InputLabel>Template</InputLabel>
                            <Select label="Template" value={debugTemplateId} onChange={e => setDebugTemplateId(e.target.value)}>
                                {templates.filter(t => t.active).map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={triggerDebugAssignment}>Assign to user</Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Template</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Reward claimed</TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(instancesPage?.items ?? []).map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        <Link
                                            component="button"
                                            type="button"
                                            underline="hover"
                                            onClick={() => setSelectedQuestId(row.id)}
                                            sx={{ font: 'inherit' }}
                                        >
                                            {row.id}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            component="button"
                                            type="button"
                                            underline="hover"
                                            onClick={() => navigate('/users/' + row.userId + '?tab=dailyQuests')}
                                            sx={{ font: 'inherit', textAlign: 'left' }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar
                                                    src={row.user?.photoUrl ? (resolveStrapiMediaUrlForDisplay(row.user.photoUrl) ?? row.user.photoUrl) : undefined}
                                                    sx={{ width: 28, height: 28 }}
                                                >
                                                    {userDisplayName(row).charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                                        <Typography variant="body2" noWrap>{userDisplayName(row)}</Typography>
                                                        {countryFlag(row.user?.countryCode) && (
                                                            <Typography component="span" variant="body2" title={row.user?.countryCode ?? undefined}>
                                                                {countryFlag(row.user?.countryCode)}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                    <Typography variant="caption" color="text.secondary">{row.userId}</Typography>
                                                </Box>
                                            </Stack>
                                        </Link>
                                    </TableCell>
                                    <TableCell>{title(row)}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{row.rewardClaimed ? 'true' : 'false'}</TableCell>
                                    <TableCell>{row.tasks.filter(t => t.status === 'COMPLETED').length} / {row.tasks.length}</TableCell>
                                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                                    <TableCell>{formatDate(row.expiresAt)}</TableCell>
                                    <TableCell align="right">
                                        {row.status === 'PENDING' && <Button color="error" onClick={() => cancelQuest(row)}>Cancel</Button>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            )}

            {tab === 2 && (
                <Stack gap={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <FormControl sx={{ minWidth: 280 }}>
                            <InputLabel>Template</InputLabel>
                            <Select
                                label="Template"
                                value={statisticsTemplateId}
                                onChange={e => setStatisticsTemplateId(e.target.value)}
                            >
                                <MenuItem value="">All templates</MenuItem>
                                {templates.map(template => (
                                    <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 180 }}>
                            <InputLabel>Timeframe</InputLabel>
                            <Select
                                label="Timeframe"
                                value={statisticsTimeframe}
                                onChange={e => setStatisticsTimeframe(e.target.value as DailyQuestStatisticsTimeframe)}
                            >
                                {STAT_TIMEFRAME_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" onClick={loadStatistics}>Refresh</Button>
                        {statisticsLoading && <CircularProgress size={24} />}
                    </Stack>

                    {statistics && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                {statistics.templateName ?? 'All templates'} · {formatDate(statistics.from)} - {formatDate(statistics.to)}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
                                <StatCard label="Created in timeframe" value={formatNumber(statistics.createdInTimeframe)} />
                                <StatCard label="Created today" value={formatNumber(statistics.createdToday)} />
                                <StatCard label="Created in last 30 days" value={formatNumber(statistics.totalCreatedLast30Days)} />
                                <StatCard label="Claimed rewards" value={`${formatNumber(statistics.totalClaimedRewardAmount)} betcoins`} />
                                <StatCard label="Pending now" value={formatNumber(statistics.pending)} />
                                <StatCard label="Completed" value={formatNumber(statistics.completed)} />
                                <StatCard label="Expired" value={formatNumber(statistics.expired)} />
                                <StatCard label="Cancelled" value={formatNumber(statistics.cancelled)} />
                                <StatCard label="Average quest completeness" value={formatPercent(statistics.averageQuestCompletenessPercent)} />
                                <StatCard label="Average task progress" value={formatPercent(statistics.averageTaskProgressPercent)} />
                                <StatCard label="Tasks completed" value={`${formatNumber(statistics.completedTasks)} / ${formatNumber(statistics.totalTasks)}`} />
                                <StatCard label="Tasks pending" value={formatNumber(statistics.pendingTasks)} />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '2fr 1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Created quests</Typography>
                                        <LineChart
                                            height={300}
                                            xAxis={[{ scaleType: 'time', data: statistics.createdChart.map(point => new Date(point.date)) }]}
                                            series={[{ data: statistics.createdChart.map(point => point.value), label: 'Created' }]}
                                        />
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Status distribution</Typography>
                                        <PieChart
                                            height={300}
                                            series={[{
                                                data: statistics.statusDistribution.map(item => ({
                                                    id: item.status,
                                                    value: item.value,
                                                    label: item.status,
                                                })),
                                            }]}
                                        />
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Claimed rewards</Typography>
                                        <LineChart
                                            height={300}
                                            xAxis={[{ scaleType: 'time', data: statistics.claimedRewardChart.map(point => new Date(point.date)) }]}
                                            series={[{ data: statistics.claimedRewardChart.map(point => point.value), label: 'Betcoins' }]}
                                        />
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Quest statuses</Typography>
                                        <BarChart
                                            height={300}
                                            xAxis={[{ scaleType: 'band', data: statistics.statusDistribution.map(item => item.status) }]}
                                            series={[{ data: statistics.statusDistribution.map(item => item.value), label: 'Quests' }]}
                                        />
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Average progress by task</Typography>
                                        <BarChart
                                            height={320}
                                            xAxis={[{ scaleType: 'band', data: statistics.taskProgress.map(item => item.taskType) }]}
                                            series={[{ data: statistics.taskProgress.map(item => item.averageProgressPercent), label: 'Progress %' }]}
                                        />
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Task completion</Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Task</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                    <TableCell align="right">Completed</TableCell>
                                                    <TableCell align="right">Pending</TableCell>
                                                    <TableCell align="right">Avg progress</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {statistics.taskProgress.map(row => (
                                                    <TableRow key={row.taskType}>
                                                        <TableCell>{row.taskType}</TableCell>
                                                        <TableCell align="right">{formatNumber(row.totalTasks)}</TableCell>
                                                        <TableCell align="right">{formatNumber(row.completedTasks)}</TableCell>
                                                        <TableCell align="right">{formatNumber(row.pendingTasks)}</TableCell>
                                                        <TableCell align="right">{formatPercent(row.averageProgressPercent)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            </Box>
                        </>
                    )}
                </Stack>
            )}

            <DailyQuestDetailsDialog
                open={selectedQuestId != null}
                questId={selectedQuestId}
                onClose={() => setSelectedQuestId(null)}
                onChanged={loadInstances}
            />
        </Box>
    );
}
